import s from './ProductItemSearch.module.css'
import { Product } from '../../../types/product'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import Image from '../Image/Image'
import ProductMarkers from '../ProductMarkers/ProductMarkers'
import {
  getEffectivePrice,
  getMinEffectiveVariant,
  getProductDisplayWeight,
  getSortedVariants,
  hasCatalogDiscount,
} from '../../../utils/cartUtils'

export default function ProductItemSearch({ product }: { product: Product }) {
  const [imageError, setImageError] = useState(false)

  const [isAddedToCart, setIsAddedToCart] = useState(false)
  const isMobile = window.innerWidth < 1024

  const isHoverCapable = (): boolean => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    try {
      return window.matchMedia('(hover: hover) and (pointer: fine)').matches
    } catch {
      return false
    }
  }

  const sortedVariants = getSortedVariants(product.prices || [])
  const displayedVariant = getMinEffectiveVariant(sortedVariants)
  const hasVariants = sortedVariants.length > 0

  const getPriceDisplay = () => {
    if (!hasVariants) return 'Ціна не вказана'
    const effectivePrice = getEffectivePrice(displayedVariant!)
    if (sortedVariants.length > 1) return `від ${effectivePrice} ₴`
    return `${effectivePrice} ₴`
  }

  const getOriginalPriceDisplay = () => {
    if (!displayedVariant || !hasCatalogDiscount(displayedVariant.discount)) return null
    return `${displayedVariant.price} ₴`
  }

  const getWeight = () => getProductDisplayWeight(product, displayedVariant)

  return (
    <>
      <Link
        to={`/product/${product.slug}`}
        className={`${s.productItem} ${isAddedToCart ? s.addToCart : ''}`}
        onMouseLeave={() => {
          if (isHoverCapable()) setIsAddedToCart(false)
        }}
      >
        <div className={s.imageWrapper}>
          <Image
            mainPicture={imageError ? undefined : product.mainPicture}
            mainPictureBig={imageError ? undefined : product.mainPictureBig}
            alt=""
            onError={() => setImageError(true)}
          />
        </div>
        <div className={s.productInfo}>
          <ProductMarkers
            className={s.productMarker}
            relative={isMobile}
            tags={product.tags && Array.isArray(product.tags) ? product.tags : []}
              discount={displayedVariant?.discount}
          />
          <div className={s.namingWrapper}>
            <h3 className={s.productName}>{product.name}</h3>
            <p className={s.productDescription}>{product.description}</p>
          </div>

          <div className={s.infoWrapper}>
            <div className={s.priceWrapper}>
              {getOriginalPriceDisplay() ? (
                <div className={s.priceDiscount}>
                  <p className={s.price}>{getPriceDisplay()}</p>
                  <p className={s.originalPrice}>{getOriginalPriceDisplay()}</p>
                </div>
              ) : (
                <p className={s.price}>{getPriceDisplay()}</p>
              )}
              {getWeight() && <p className={s.packagingWeight}>/ {getWeight()}</p>}
            </div>
          </div>
        </div>
      </Link>
    </>
  )
}

export const doneIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
      <rect x="1" y="1" width="20" height="20" rx="10" stroke="white" strokeWidth="1.5" />
      <path
        d="M8 11H14"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11 7.99988L11 13.9999"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export const addedIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <rect width="20" height="20" rx="10" fill="white" />
      <path
        d="M13.0065 6.86366C13.2491 6.86243 13.4868 6.93466 13.6912 7.07168C13.8056 7.14807 13.8951 7.25922 13.9477 7.39031C14.0003 7.5214 14.0136 7.66621 13.9858 7.80543C13.8971 8.11412 13.7328 8.39302 13.5093 8.61429C13.0661 9.10656 12.6115 9.68562 12.2094 10.2161C11.6577 10.9438 11.0791 11.6344 10.5469 12.4095C10.1353 13.0091 9.45438 14.0308 8.84551 14.1262C8.16865 14.2322 7.6567 13.4878 7.32591 12.8728C6.85654 12.0698 6.43361 11.238 6.05941 10.3818C5.99018 10.1964 5.98119 9.99234 6.03383 9.80111C6.08647 9.60988 6.1978 9.44222 6.35055 9.32414C6.5033 9.20606 6.68893 9.14416 6.87864 9.14804C7.06835 9.15192 7.25154 9.22137 7.39978 9.34562C7.85647 9.73934 8.19898 10.4652 8.51749 10.9851C8.63326 11.1741 8.6506 11.2649 8.93634 10.9197C9.61236 10.1032 10.1855 9.44705 10.8725 8.64486C11.3322 8.10808 11.725 7.46177 12.3238 7.07861C12.5269 6.93953 12.764 6.86489 13.0065 6.86366Z"
        fill="#FF7000"
      />
    </svg>
  )
}

export const cartIcon = () => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M12.0007 10.0002H4.66732L3.66732 4.00016H14.0007L12.0007 10.0002Z" fill="white" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.33398 2.00016C1.33398 1.63197 1.63246 1.3335 2.00065 1.3335H3.33398C3.65988 1.3335 3.938 1.5691 3.99158 1.89056L4.23207 3.3335H14.0007C14.2149 3.3335 14.4162 3.43651 14.5415 3.61036C14.6668 3.7842 14.7009 4.00768 14.6331 4.21098L12.6331 10.211C12.5424 10.4832 12.2876 10.6668 12.0007 10.6668H4.66732C4.34142 10.6668 4.0633 10.4312 4.00972 10.1098L2.76923 2.66683H2.00065C1.63246 2.66683 1.33398 2.36835 1.33398 2.00016ZM4.45429 4.66683L5.23207 9.3335H11.5201L13.0757 4.66683H4.45429Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.00065 13.3335C4.00065 12.5971 4.5976 12.0002 5.33398 12.0002C6.07036 12.0002 6.66732 12.5971 6.66732 13.3335C6.66732 14.0699 6.07036 14.6668 5.33398 14.6668C4.5976 14.6668 4.00065 14.0699 4.00065 13.3335Z"
        fill="white"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10.0007 13.3335C10.0007 12.5971 10.5976 12.0002 11.334 12.0002C12.0704 12.0002 12.6673 12.5971 12.6673 13.3335C12.6673 14.0699 12.0704 14.6668 11.334 14.6668C10.5976 14.6668 10.0007 14.0699 10.0007 13.3335Z"
        fill="white"
      />
    </svg>
  )
}
